import { ReportContextFields } from '../ReportContext';
import { type Priority } from '@lixionary-jira/domain';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import type { TicketDetail } from '../../../api/ticket-types';
import { Button } from '../../../ui/Button';
import { Alert, ErrorAlert } from '../../../ui/feedback';
import { Checkbox, Field, Input, ListEditor, Segmented, Select, Textarea } from '../../../ui/forms';
import { Card } from '../../../ui/misc';
import { Modal } from '../../../ui/Modal';
import { PRIORITY_OPTIONS } from '../bits';

export function Triage({ t }: { t: TicketDetail }) {
  const people = useQuery({
    queryKey: ['people', t.project.key],
    queryFn: () => api.get<{ id: string; displayName: string }[]>(`/projects/${t.project.key}/people`),
  });
  const [priority, setPriority] = useState<Priority>(t.priority);
  const [assignee, setAssignee] = useState(t.assignee?.id ?? '');
  const [labels, setLabels] = useState(t.labels.join(', '));
  useEffect(() => {
    setPriority(t.priority);
    setAssignee(t.assignee?.id ?? '');
    setLabels(t.labels.join(', '));
  }, [t.priority, t.assignee?.id, t.labels]);
  const save = useAction(
    () =>
      api.patch(`/projects/${t.project.key}/tickets/${t.number}/triage`, {
        priority,
        assigneeUserId: assignee || null,
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        expectedVersion: t.version,
      }),
    {
      success: 'Triage saved',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
    },
  );
  return (
    <Card title="Triage" subtitle="Doesn't change scope or acceptance." tight>
      <Field label="Priority">
        {(p) => (
          <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Assignee" hint="Unassigned todo work is AI-claimable.">
        {(p) => (
          <Select {...p} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {(people.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Labels">
        {(p) => <Input {...p} value={labels} onChange={(e) => setLabels(e.target.value)} />}
      </Field>
      <ErrorAlert error={save.error} />
      <Button size="sm" onClick={() => save.mutate(undefined)} loading={save.isPending}>
        Save triage
      </Button>
    </Card>
  );
}

export function EditScope({ t, onClose }: { t: TicketDetail; onClose: () => void }) {
  const [type, setType] = useState(t.type);
  const [title, setTitle] = useState(t.title);
  const [description, setDescription] = useState(t.description);
  const [steps, setSteps] = useState(t.stepsOrOutcome);
  const [context, setContext] = useState({
    screenName: t.screenName,
    screenUrl: t.screenUrl,
    exampleData: t.exampleData,
  });
  const [criteria, setCriteria] = useState(t.acceptanceCriteria.length ? t.acceptanceCriteria : ['']);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const accepted = t.status !== 'pending';
  const save = useAction(
    () =>
      api.patch(`/projects/${t.project.key}/tickets/${t.number}/scope`, {
        type,
        title,
        description,
        stepsOrOutcome: steps,
        ...context,
        acceptanceCriteria: criteria.filter((c) => c.trim()),
        expectedVersion: t.version,
        confirmReturnToPending: confirm,
        reason: reason || undefined,
      }),
    {
      success: 'Scope saved as a new revision',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
      onSuccess: onClose,
    },
  );
  const conflict = save.error instanceof ApiError && save.error.code === 'version_conflict';
  return (
    <Modal
      title={`Edit scope · ${t.key}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate(undefined)}
            loading={save.isPending}
            disabled={accepted && (!confirm || !reason.trim())}
          >
            {accepted ? 'Return to Pending and save' : 'Save new revision'}
          </Button>
        </>
      }
    >
      {accepted ? (
        <Alert tone="warning" title="This scope is accepted">
          <span>
            Saving a material change returns {t.key} to Pending, revokes acceptance and claims, and
            invalidates readiness and release approvals. Prior evidence stays in history.
          </span>
        </Alert>
      ) : (
        <p className="muted small">
          Saving creates scope revision {t.scopeRevision + 1}. Outstanding acceptance requests for revision{' '}
          {t.scopeRevision} become stale.
        </p>
      )}
      <Segmented
        label="Type"
        value={type}
        onChange={setType}
        options={[
          { value: 'issue', label: 'Issue' },
          { value: 'feature_request', label: 'Feature request' },
        ]}
      />
      <Field label="Title">
        {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="Description">
        {(p) => (
          <Textarea {...p} rows={6} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <Field label={type === 'issue' ? 'Reproduction steps' : 'Intended outcome'} optional>
        {(p) => <Textarea {...p} rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} />}
      </Field>
      <ReportContextFields
        value={context}
        onChange={(value) =>
          setContext({
            screenName: value.screenName ?? '',
            screenUrl: value.screenUrl ?? '',
            exampleData: value.exampleData ?? '',
          })
        }
      />
      <div className="field">
        <span className="label">Acceptance criteria</span>
        <ListEditor label="Criterion" items={criteria} onChange={setCriteria} addLabel="Add criterion" />
      </div>
      {accepted && (
        <>
          <Field label="Reason">
            {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <Checkbox
            label="I understand this returns the ticket to Pending"
            checked={confirm}
            onChange={setConfirm}
          />
        </>
      )}
      {conflict && (
        <Alert tone="warning">
          Someone changed this ticket while you were editing. Your text is kept here; reload the page in
          another tab to compare, then save again.
        </Alert>
      )}
      <ErrorAlert error={conflict ? null : save.error} />
    </Modal>
  );
}
